/**
 * The typing side of service-call billing (D86): dollars in the boxes, cents
 * to the server, the example line under the business rates, the contractor
 * rate fields, the job form's "how this bills" default, and the price-book
 * below-cost check. Pure, so each is tested; the arithmetic itself stays in
 * `servicecall.ts`.
 */
import { parseDollars } from "./lineprice";
import { type Rates, charge, totalCents } from "./servicecall";

export const DEFAULT_MINUTES_INCLUDED = 30;
export const DEFAULT_KM_INCLUDED = 50;
const MAX_MINUTES_INCLUDED = 600;
const MAX_KM_INCLUDED = 10_000;
const MAX_KM_RATE_CENTS = 100_000;

/** Cents as the text of a dollars box: 11000 -> "110.00". */
export function dollarsText(cents: number | null | undefined): string {
  return cents === null || cents === undefined ? "" : (cents / 100).toFixed(2);
}

/** A whole number as typed (minutes, km), or null if it isn't one in range. */
export function parseWhole(text: string, max: number): number | null {
  const t = text.trim();
  if (!/^\d+$/.test(t)) return null;
  const n = Number(t);
  return n <= max ? n : null;
}

/* ------------------------------------------------------- business settings */

export type ServiceText = {
  fee: string;
  minutes: string;
  hourly: string;
  km: string;
  kmRate: string;
};

export type ServiceBody = {
  service_call_fee_cents: number;
  service_call_minutes_included: number;
  service_hourly_rate_cents: number;
  service_km_included: number;
  service_km_rate_cents: number;
};

type OrgRates = {
  service_call_fee_cents?: number;
  service_call_minutes_included?: number;
  service_hourly_rate_cents?: number;
  service_km_included?: number;
  service_km_rate_cents?: number;
};

export function serviceTextFrom(org: OrgRates): ServiceText {
  return {
    fee: dollarsText(org.service_call_fee_cents ?? 0),
    minutes: String(org.service_call_minutes_included ?? DEFAULT_MINUTES_INCLUDED),
    hourly: dollarsText(org.service_hourly_rate_cents ?? 0),
    km: String(org.service_km_included ?? DEFAULT_KM_INCLUDED),
    kmRate: dollarsText(org.service_km_rate_cents ?? 0),
  };
}

/** A dollars box where blank means 0 (not set up); null when it isn't money. */
function dollarsOrZero(text: string, maxCents?: number): number | null {
  if (text.trim() === "") return 0;
  const c = parseDollars(text);
  return c !== null && (maxCents === undefined || c <= maxCents) ? c : null;
}

/** What the five boxes say as cents, per field; null where the text isn't valid. */
export function parseService(text: ServiceText): { [K in keyof ServiceText]: number | null } {
  return {
    fee: dollarsOrZero(text.fee),
    minutes: parseWhole(text.minutes, MAX_MINUTES_INCLUDED),
    hourly: dollarsOrZero(text.hourly),
    km: parseWhole(text.km, MAX_KM_INCLUDED),
    kmRate: dollarsOrZero(text.kmRate, MAX_KM_RATE_CENTS),
  };
}

/** The PATCH body, or null when any box is not valid. */
export function serviceBody(text: ServiceText): ServiceBody | null {
  const p = parseService(text);
  if (p.fee === null || p.minutes === null || p.hourly === null || p.km === null || p.kmRate === null) return null;
  return {
    service_call_fee_cents: p.fee,
    service_call_minutes_included: p.minutes,
    service_hourly_rate_cents: p.hourly,
    service_km_included: p.km,
    service_km_rate_cents: p.kmRate,
  };
}

/** Which boxes are not valid, for marking them. */
export function serviceInvalid(text: ServiceText): (keyof ServiceText)[] {
  const p = parseService(text);
  return (Object.keys(p) as (keyof ServiceText)[]).filter((k) => p[k] === null);
}

export const EXAMPLE_MINUTES = 60;
export const EXAMPLE_KM_ONE_WAY = 40;

/**
 * "1 hr on site, 40 km away" at the typed rates, in cents; null when no fee and
 * no hourly rate is set (nothing to show). A box that isn't valid yet falls
 * back to what is saved, so the example never goes blank mid-typing.
 */
export function exampleCents(text: ServiceText, saved: ServiceText): number | null {
  const typed = parseService(text);
  const fallback = parseService(saved);
  const pick = (k: keyof ServiceText): number => typed[k] ?? fallback[k] ?? 0;
  const rates: Rates = {
    fee_cents: pick("fee"),
    minutes_included: pick("minutes"),
    hourly_cents: pick("hourly"),
    km_included: pick("km"),
    km_rate_cents: pick("kmRate"),
  };
  if (rates.fee_cents <= 0 && rates.hourly_cents <= 0) return null;
  return totalCents(charge(EXAMPLE_MINUTES, EXAMPLE_KM_ONE_WAY, rates));
}

/* ------------------------------------------------------------- contractors */

export type ContractorText = { fee: string; hourly: string; km: string };

export type ContractorBody = {
  is_contractor: boolean;
  contractor_fee_cents: number | null;
  contractor_hourly_rate_cents: number | null;
  contractor_km_rate_cents: number | null;
};

export function contractorTextFrom(c: {
  contractor_fee_cents?: number | null;
  contractor_hourly_rate_cents?: number | null;
  contractor_km_rate_cents?: number | null;
}): ContractorText {
  return {
    fee: dollarsText(c.contractor_fee_cents),
    hourly: dollarsText(c.contractor_hourly_rate_cents),
    km: dollarsText(c.contractor_km_rate_cents),
  };
}

/** Blank is null (use the business's rate); text that isn't money is undefined. */
function optionalDollars(text: string, maxCents?: number): number | null | undefined {
  if (text.trim() === "") return null;
  const c = parseDollars(text);
  return c !== null && (maxCents === undefined || c <= maxCents) ? c : undefined;
}

/** The contractor part of a customer's body, or null when a rate isn't valid money. */
export function contractorBody(isContractor: boolean, text: ContractorText): ContractorBody | null {
  const fee = optionalDollars(text.fee);
  const hourly = optionalDollars(text.hourly);
  const km = optionalDollars(text.km, MAX_KM_RATE_CENTS);
  if (fee === undefined || hourly === undefined || km === undefined) return null;
  return {
    is_contractor: isContractor,
    contractor_fee_cents: fee,
    contractor_hourly_rate_cents: hourly,
    contractor_km_rate_cents: km,
  };
}

/** "uses $120.00" under a blank contractor rate; says so when the business has none yet. */
export function usesPlaceholder(businessCents: number | null | undefined, format: (c: number) => string): string {
  return businessCents ? `uses ${format(businessCents)}` : "uses the business rate (not set)";
}

/* ----------------------------------------------------------- job creation */

export type BillingMode = "service_call" | "contractor" | "time_and_materials";

export const BILLING_LABEL: Record<BillingMode, string> = {
  service_call: "Service call",
  contractor: "Contractor",
  time_and_materials: "Time & materials",
};

/** What "how this bills" starts as: contractor customer, else a service call for service/callout, else T&M. */
export function defaultBillingMode(isContractor: boolean, jobType: string): BillingMode {
  if (isContractor) return "contractor";
  return jobType === "service" || jobType === "callout" ? "service_call" : "time_and_materials";
}

/** Blank is "work it out from the address" (undefined); otherwise km >= 0, or null if not a number. */
export function parseKmOneWay(text: string): number | null | undefined {
  const t = text.trim();
  if (t === "") return undefined;
  if (!/^\d*\.?\d*$/.test(t) || t === ".") return null;
  const n = Number(t);
  return Number.isFinite(n) && n <= 5000 ? n : null;
}

/* ------------------------------------------------------------- price book */

/** PRICE-2: a fixed sell price that is under the cost. Other modes can't be judged from the form. */
export function sellsBelowCost(mode: string, sellText: string, sellCents: number, costCents: number): boolean {
  return mode === "fixed" && sellText.trim() !== "" && sellCents > 0 && sellCents < costCents;
}
